import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { usePoints } from '../../hooks/usePoints';
import { useSlotMachine } from '../../hooks/useSlotMachine';
import { validators } from '../../utils/validators';
import { formatters } from '../../utils/formatters';
import SlotMachineAnimation from '../common/SlotMachineAnimation';

const AddPointsForm = ({ customer, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    points: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { addPoints } = usePoints();
  const { animationData, showPointsAnimation, hideAnimation, resetAnimation } = useSlotMachine();

  const validateForm = () => {
    const newErrors = {};

    if (!validators.isValidPoints(formData.points)) {
      newErrors.points = 'Digite uma quantidade válida de pontos';
    }

    if (formData.description.length > 200) {
      newErrors.description = 'Descrição deve ter no máximo 200 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const oldPoints = customer.points || 0;
      const pointsToAdd = parseInt(formData.points);
      const newPoints = oldPoints + pointsToAdd;
      
      // Reset qualquer animação anterior
      resetAnimation();
      
      await addPoints(customer.id, pointsToAdd, formData.description);
      
      // Aguardar um pouco antes de mostrar a animação
      setTimeout(() => {
        showPointsAnimation(oldPoints, newPoints, 3500);
      }, 100);
      
      // Aguardar animação terminar antes de chamar onSuccess
      setTimeout(() => {
        onSuccess();
      }, 4000); // 4 segundos total para dar tempo da animação e som completarem
      
    } catch (error) {
      alert('Erro ao adicionar pontos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-sm text-gray-600">Cliente</p>
        <p className="text-lg font-bold text-gray-900">{customer.name}</p>
        <p className="text-sm text-gray-600">
          {formatters.formatCpfOrCnpj(customer.cpf)}
          {customer.cpf && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {customer.cpf.length === 11 ? 'CPF' : customer.cpf.length === 14 ? 'CNPJ' : 'Doc'}
            </span>
          )}
        </p>
        {customer.phone && (
          <p className="text-sm text-gray-600">{formatters.formatPhone(customer.phone)}</p>
        )}
        {customer.email && (
          <p className="text-sm text-gray-600">{customer.email}</p>
        )}
        <div className="mt-2">
          <p className="text-sm text-gray-600">Saldo atual</p>
          <p className="text-2xl font-bold text-gray-900">{customer.points} pontos</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pontos a adicionar *
          </label>
          <input
            type="number"
            value={formData.points}
            onChange={(e) => setFormData({ ...formData, points: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.points ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Digite a quantidade de pontos"
            min="1"
            max="10000"
          />
          {errors.points && <p className="text-sm text-red-600 mt-1">{errors.points}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrição
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.description ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Ex: Compra, Promoção, Indicação... (opcional)"
            maxLength="200"
          />
          {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
          <p className="text-xs text-gray-500 mt-1">
            {formData.description.length}/200 caracteres
          </p>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Pontos</span>
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>

      {/* Animação de Caça-níquel */}
      <SlotMachineAnimation
        isVisible={animationData.isVisible}
        onClose={hideAnimation}
        type={animationData.type}
        oldValue={animationData.oldValue}
        newValue={animationData.newValue}
        rewardName={animationData.rewardName}
        duration={animationData.duration}
      />
    </div>
  );
};

export default AddPointsForm;